import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { TenantLogo } from '@/components/shell/TenantLogo';

/**
 * Branded interstitial shown when the signed-in identity cannot access this host (PTV2-1383). With a
 * single shared browser IdP session across hosts, opening a tenant while signed into the global
 * cluster console (or another tenant) SSO's the wrong identity onto this host; the gateway rejects it
 * (tenant mismatch) and redirects the browser here instead of failing. The page offers exactly one
 * action:
 *
 * <ul>
 *   <li><b>Sign out and sign in here</b> — follow the gateway-supplied {@code signout} URL, which ends
 *       the shared IdP session and returns to this host so they can sign in as a local user. When no
 *       sign-out URL is supplied, fall back to re-initiating login for this host.</li>
 * </ul>
 *
 * <p>PTV2-1383: it deliberately offers <b>no</b> "go to cluster administration" bridge — a tenant login
 * door must never be a route into the platform space, even though the two share one IdP. The only way
 * on from here is to sign out (which logs OUT) and sign in with a workspace account.</p>
 *
 * <p>Rendered by {@link SessionGuard} on the {@code /session-conflict} path BEFORE the auth gate, since
 * the user has no session on this host — so query params are read from {@code window.location} rather
 * than the router.</p>
 */
export function SessionConflictPage(): ReactElement {
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const isClusterAdmin = params.get('reason') === 'cluster_admin';
  const signOutUrl = params.get('signout');

  const heading = isClusterAdmin
    ? t('auth.sessionConflict.clusterAdmin.heading')
    : t('auth.sessionConflict.otherAccount.heading');
  const message = isClusterAdmin
    ? t('auth.sessionConflict.clusterAdmin.message')
    : t('auth.sessionConflict.otherAccount.message');

  const signOutAndSignIn = (): void => {
    // The gateway builds a sign-out URL whose id_token_hint matches the live IdP session, so the OP
    // honours it and returns here for a fresh login. Fall back to re-initiating login for this host.
    window.location.href =
      signOutUrl !== null && signOutUrl.length > 0
        ? signOutUrl
        : `/oauth2/authorization/${window.location.hostname}`;
  };

  return (
    <main
      role="alert"
      className="flex min-h-screen items-center justify-center bg-muted/30 p-6"
    >
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <div className="mb-4 flex items-center gap-2.5">
            <TenantLogo />
            <span className="text-lg font-semibold tracking-tight text-brand">
              {t('shell.rail.brandName')}
            </span>
          </div>
          <CardTitle>{heading}</CardTitle>
          <CardDescription>{message}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-2">
          <Button type="button" className="w-full" onClick={signOutAndSignIn}>
            {t('auth.sessionConflict.signOutAndSignIn')}
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
